import tokens from '@kluein/design-tokens';
import { Banner } from '@kluein/klue-ui';
import {
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { CalendarBlank, X, Check } from '@phosphor-icons/react';
import { addDays, format, isValid, parseISO } from 'date-fns';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  SCHEDULE_FREQUENCY,
  FREQUENCY_DAYS,
  DAYS_TO_FREQUENCY,
} from 'api/types/card-schedule.types';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { useResponsive } from 'hooks/use-responsive';
import { useCardSchedule } from 'hooks/useCardSchedule';
import { useGetResearchModeSourcesQuery } from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/research-sources.api';
import { useUpdateCardMutation } from 'store/api/cards/cards.api';
import { useGetUserByIdQuery } from 'store/api/users/users.api';
import TEST_IDS from 'test-ids';

import CardScheduleContent from './CardScheduleContent';
import S from './CardScheduleModal.module.css';

import type { ScheduleFrequency } from 'api/types/card-schedule.types';
import type { Dispatch } from 'store/store.types';

type CardScheduleModalProps = {
  cardId: number;
  onSave: (
    frequency: ScheduleFrequency,
    prompt: string,
    researchMode: boolean,
  ) => void;
  onClose: () => void;
};

export default function CardScheduleModal({
  cardId,
  onSave,
  onClose,
}: CardScheduleModalProps) {
  const { t } = useTranslation(['Card', 'Common']);
  const { notify } = useNotifier();
  const { maxWidth } = useResponsive();
  const dispatch = useDispatch<Dispatch>();

  // Use the new hook to get persisted schedule data
  const { schedule, hasSchedule, isLoading, isSaving, saveSchedule } =
    useCardSchedule(cardId);

  // KlueAI user for card authorship when scheduling is enabled
  const { data: klueAIUser } = useGetUserByIdQuery('klue_ai');
  const [updateCard] = useUpdateCardMutation();

  const [selectedFrequency, setSelectedFrequency] = useState<ScheduleFrequency>(
    SCHEDULE_FREQUENCY.UNSCHEDULED,
  );
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [selectedResearchMode, setSelectedResearchMode] =
    useState<boolean>(false);
  const [saveError, setSaveError] = useState(false);
  const [updateOnSave, setUpdateOnSave] = useState(false);

  // Update local state when schedule data loads
  useEffect(() => {
    if (schedule) {
      setSelectedFrequency(
        (schedule.frequencyDays != null
          ? DAYS_TO_FREQUENCY[schedule.frequencyDays]
          : undefined) ?? SCHEDULE_FREQUENCY.UNSCHEDULED,
      );
      setSelectedPrompt(schedule.prompt);
      setSelectedResearchMode(schedule.researchMode);
      setModernDisabledSources(schedule.disabledSourceTypes);
      setUpdateOnSave(false);
    }
  }, [schedule]);

  // Reset the "update on save" toggle if the user switches back to unscheduled,
  // so we don't send lastUpdatedAt: null on a save path that won't update anyway.
  useEffect(() => {
    if (selectedFrequency === SCHEDULE_FREQUENCY.UNSCHEDULED) {
      setUpdateOnSave(false);
    }
  }, [selectedFrequency]);

  // Derive the next-update preview from the user's *current* selection, not the
  // persisted schedule, so the footer reflects in-flight changes in this modal.
  const selectedFrequencyDays =
    selectedFrequency === SCHEDULE_FREQUENCY.UNSCHEDULED
      ? null
      : FREQUENCY_DAYS[selectedFrequency];

  const wasUnscheduled = schedule?.frequencyDays == null;

  const persistedLastUpdatedAtDate =
    schedule?.lastUpdatedAt != null ? parseISO(schedule.lastUpdatedAt) : null;
  const hasValidPersistedLastUpdatedAt =
    persistedLastUpdatedAtDate != null && isValid(persistedLastUpdatedAtDate);

  // Show the footer whenever the user has picked an active frequency. If we
  // have a known last-update timestamp we can compute the next date; otherwise
  // we fall back to "Soon".
  const showNextUpdateFooter = selectedFrequencyDays != null;

  const nextUpdateComputedDate =
    selectedFrequencyDays != null && hasValidPersistedLastUpdatedAt
      ? format(
          addDays(persistedLastUpdatedAtDate, selectedFrequencyDays),
          'MMM d, yyyy',
        )
      : null;

  // The "update on save" checkbox only affects the payload when the persisted
  // schedule already has both an active frequency AND a known lastUpdatedAt
  // we'd be clearing — in any other state, toggling it is a no-op.
  const showUpdateOnSaveCheckbox =
    selectedFrequencyDays != null &&
    schedule?.frequencyDays != null &&
    hasValidPersistedLastUpdatedAt;

  // State for disabled sources
  const [modernDisabledSources, setModernDisabledSources] = useState<string[]>(
    [],
  );

  // The schedule modal is curator-only, and curators editing an
  // answer-mode schedule get a cards+alerts-only surface from the BE
  // with no UI filtering — so we only fetch sources for research-mode
  // schedules. For answer-mode schedules `sourcesData` stays undefined
  // and CardScheduleContent's existing `(sources?.length ?? 0) > 0`
  // gate hides the filter button.
  //
  // Drive the skip off the persisted `schedule?.researchMode` rather
  // than the local `selectedResearchMode` state. The local state
  // defaults to false and is synced from the loaded schedule via the
  // useEffect above — that sync happens after the first render, so
  // reading the local state here would briefly fire the research-mode
  // query for answer-mode schedules before re-skipping it.
  const { data: sourcesData, isLoading: isLoadingSources } =
    useGetResearchModeSourcesQuery(undefined, {
      skip: schedule?.researchMode !== true,
    });

  const handleSave = async () => {
    try {
      setSaveError(false);

      // Only persist if we have a valid frequency (not unscheduled)
      if (selectedFrequency !== SCHEDULE_FREQUENCY.UNSCHEDULED) {
        await saveSchedule({
          prompt: selectedPrompt,
          researchMode: selectedResearchMode,
          disabledSourceTypes: modernDisabledSources,
          frequencyDays:
            FREQUENCY_DAYS[
              selectedFrequency as Exclude<ScheduleFrequency, 'unscheduled'>
            ],
          ...(wasUnscheduled && {
            lastUpdatedAt: new Date().toISOString(),
          }),
          ...(updateOnSave &&
            !wasUnscheduled && {
              lastUpdatedAt: null,
            }),
        });
      } else if (hasSchedule) {
        // If changing to unscheduled and a schedule exists, update with null frequency
        await saveSchedule({
          prompt: selectedPrompt,
          researchMode: selectedResearchMode,
          disabledSourceTypes: modernDisabledSources,
          frequencyDays: null,
        });
      }

      // Set card author to KlueAI when newly enabling scheduling
      if (
        wasUnscheduled &&
        selectedFrequency !== SCHEDULE_FREQUENCY.UNSCHEDULED &&
        klueAIUser?.id
      ) {
        const resultArray = await updateCard({
          author: klueAIUser.id,
          cardIds: [cardId],
        }).unwrap();
        if (resultArray?.length) {
          dispatch.cards.updateAndParseCardsData({
            updatedCards: [resultArray[0]],
          });
        }
      }

      notify({
        type: NotifierType.SUCCESS,
        message: t('Card:schedule.savedSuccess'),
        icon: <Check />,
      });

      onSave(selectedFrequency, selectedPrompt, selectedResearchMode);
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen={maxWidth.medium}
      centered
      title={
        <Group gap="sm" align="center">
          <CalendarBlank />
          <Title order={2} size="h4">
            {t('Card:schedule.title')}
          </Title>
        </Group>
      }
      closeButtonProps={{
        icon: <X />,
      }}
      classNames={{
        content: S.modalContent,
        header: S.modalHeader,
        title: S.modalTitle,
        close: S.modalClose,
        body: S.modalBody,
      }}
      data-test-id={TEST_IDS.cardSchedule.modal}
    >
      <Stack
        gap="md"
        py={tokens.spacing.medium}
        px={tokens.spacing.xlarge}
        className={S.scrollArea}
      >
        <CardScheduleContent
          prompt={selectedPrompt}
          onPromptChange={setSelectedPrompt}
          frequency={selectedFrequency}
          onFrequencyChange={setSelectedFrequency}
          researchMode={selectedResearchMode}
          onResearchModeChange={setSelectedResearchMode}
          disabledSources={modernDisabledSources}
          onDisabledSourcesChange={setModernDisabledSources}
          sources={sourcesData?.sources}
          isLoadingSources={isLoadingSources}
        />

        {saveError && (
          <Banner
            variant="inline"
            status="error"
            message={t('Card:schedule.savedError')}
            show
          />
        )}
      </Stack>

      <Group
        justify="space-between"
        align="center"
        gap="sm"
        py={tokens.spacing.medium}
        px={tokens.spacing.xlarge}
        className={S.footer}
      >
        {showNextUpdateFooter ? (
          <Stack gap="xs">
            <Text
              size="md"
              data-test-id={TEST_IDS.cardSchedule.nextUpdateLabel}
            >
              {t('Card:schedule.nextUpdate', {
                date:
                  (showUpdateOnSaveCheckbox && updateOnSave) ||
                  nextUpdateComputedDate == null
                    ? t('Card:schedule.nextUpdateSoon')
                    : nextUpdateComputedDate,
              })}
            </Text>
            {showUpdateOnSaveCheckbox && (
              <Checkbox
                label={t('Card:schedule.updateOnSave')}
                checked={updateOnSave}
                onChange={(e) => setUpdateOnSave(e.currentTarget.checked)}
                data-test-id={TEST_IDS.cardSchedule.updateOnSaveCheckbox}
              />
            )}
          </Stack>
        ) : (
          <div />
        )}
        <Group gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
              e.preventDefault()
            }
            onClick={onClose}
            data-test-id={TEST_IDS.cardSchedule.cancelButton}
          >
            {t('Common:actions.cancel')}
          </Button>
          <Button
            variant="filled"
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
              e.preventDefault()
            }
            onClick={handleSave}
            loading={isSaving}
            disabled={selectedPrompt.trim() === ''}
            data-test-id={TEST_IDS.cardSchedule.saveButton}
          >
            {t('Common:actions.save')}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
